/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import isComplexArray from '@stdlib/array-base-assert-is-complex-typed-array';
import isBooleanArray from '@stdlib/array-base-assert-is-booleanarray';
import { isPrimitive as isBoolean } from '@stdlib/assert-is-boolean';
import isComplexLike from '@stdlib/assert-is-complex-like';
import iterationOrder from '@stdlib/ndarray-base-iteration-order';
import minmaxViewBufferIndex from '@stdlib/ndarray-base-minmax-view-buffer-index';
import ndarray2object from '@stdlib/ndarray-base-ndarraylike2object';
import reinterpretComplex from '@stdlib/strided-base-reinterpret-complex';
import reinterpretBoolean from '@stdlib/strided-base-reinterpret-boolean';
import real from '@stdlib/complex-float64-real';
import imag from '@stdlib/complex-float64-imag';
import gscal from '@stdlib/blas-base-gscal';
import blockedaccessorincludes2d from './2d_blocked_accessors.js';
import blockedaccessorincludes3d from './3d_blocked_accessors.js';
import blockedaccessorincludes4d from './4d_blocked_accessors.js';
import blockedaccessorincludes5d from './5d_blocked_accessors.js';
import blockedaccessorincludes6d from './6d_blocked_accessors.js';
import blockedaccessorincludes7d from './7d_blocked_accessors.js';
import blockedaccessorincludes8d from './8d_blocked_accessors.js';
import blockedaccessorincludes9d from './9d_blocked_accessors.js';
import blockedaccessorincludes10d from './10d_blocked_accessors.js'; // eslint-disable-line id-length
import blockedcomplexincludes2d from './2d_blocked_complex.js';
import blockedcomplexincludes3d from './3d_blocked_complex.js';
import blockedcomplexincludes4d from './4d_blocked_complex.js';
import blockedcomplexincludes5d from './5d_blocked_complex.js';
import blockedcomplexincludes6d from './6d_blocked_complex.js';
import blockedcomplexincludes7d from './7d_blocked_complex.js';
import blockedcomplexincludes8d from './8d_blocked_complex.js';
import blockedcomplexincludes9d from './9d_blocked_complex.js';
import blockedcomplexincludes10d from './10d_blocked_complex.js';
import blockedincludes2d from './2d_blocked.js';
import blockedincludes3d from './3d_blocked.js';
import blockedincludes4d from './4d_blocked.js';
import blockedincludes5d from './5d_blocked.js';
import blockedincludes6d from './6d_blocked.js';
import blockedincludes7d from './7d_blocked.js';
import blockedincludes8d from './8d_blocked.js';
import blockedincludes9d from './9d_blocked.js';
import blockedincludes10d from './10d_blocked.js';
import accessorincludes0d from './0d_accessors.js';
import accessorincludes1d from './1d_accessors.js';
import accessorincludes2d from './2d_accessors.js';
import accessorincludes3d from './3d_accessors.js';
import accessorincludes4d from './4d_accessors.js';
import accessorincludes5d from './5d_accessors.js';
import accessorincludes6d from './6d_accessors.js';
import accessorincludes7d from './7d_accessors.js';
import accessorincludes8d from './8d_accessors.js';
import accessorincludes9d from './9d_accessors.js';
import accessorincludes10d from './10d_accessors.js';
import accessorincludesnd from './nd_accessors.js';
import complexincludes0d from './0d_complex.js';
import complexincludes1d from './1d_complex.js';
import complexincludes2d from './2d_complex.js';
import complexincludes3d from './3d_complex.js';
import complexincludes4d from './4d_complex.js';
import complexincludes5d from './5d_complex.js';
import complexincludes6d from './6d_complex.js';
import complexincludes7d from './7d_complex.js';
import complexincludes8d from './8d_complex.js';
import complexincludes9d from './9d_complex.js';
import complexincludes10d from './10d_complex.js';
import complexincludesnd from './nd_complex.js';
import includes0d from './0d.js';
import includes1d from './1d.js';
import includes2d from './2d.js';
import includes3d from './3d.js';
import includes4d from './4d.js';
import includes5d from './5d.js';
import includes6d from './6d.js';
import includes7d from './7d.js';
import includes8d from './8d.js';
import includes9d from './9d.js';
import includes10d from './10d.js';
import includesnd from './nd.js';


// VARIABLES //

var INCLUDES = [
	includes0d,
	includes1d,
	includes2d,
	includes3d,
	includes4d,
	includes5d,
	includes6d,
	includes7d,
	includes8d,
	includes9d,
	includes10d
];
var ACCESSOR_INCLUDES = [
	accessorincludes0d,
	accessorincludes1d,
	accessorincludes2d,
	accessorincludes3d,
	accessorincludes4d,
	accessorincludes5d,
	accessorincludes6d,
	accessorincludes7d,
	accessorincludes8d,
	accessorincludes9d,
	accessorincludes10d
];
var COMPLEX_INCLUDES = [
	complexincludes0d,
	complexincludes1d,
	complexincludes2d,
	complexincludes3d,
	complexincludes4d,
	complexincludes5d,
	complexincludes6d,
	complexincludes7d,
	complexincludes8d,
	complexincludes9d,
	complexincludes10d
];
var BLOCKED_INCLUDES = [
	blockedincludes2d, // 0
	blockedincludes3d,
	blockedincludes4d,
	blockedincludes5d,
	blockedincludes6d,
	blockedincludes7d,
	blockedincludes8d,
	blockedincludes9d,
	blockedincludes10d // 8
];
var BLOCKED_ACCESSOR_INCLUDES = [
	blockedaccessorincludes2d, // 0
	blockedaccessorincludes3d,
	blockedaccessorincludes4d,
	blockedaccessorincludes5d,
	blockedaccessorincludes6d,
	blockedaccessorincludes7d,
	blockedaccessorincludes8d,
	blockedaccessorincludes9d,
	blockedaccessorincludes10d // 8
];
var BLOCKED_COMPLEX_INCLUDES = [
	blockedcomplexincludes2d, // 0
	blockedcomplexincludes3d,
	blockedcomplexincludes4d,
	blockedcomplexincludes5d,
	blockedcomplexincludes6d,
	blockedcomplexincludes7d,
	blockedcomplexincludes8d,
	blockedcomplexincludes9d,
	blockedcomplexincludes10d // 8
];
var MAX_DIMS = INCLUDES.length - 1;


// MAIN //

/**
* Tests whether an ndarray contains a specified value.
*
* ## Notes
*
* -   A provided ndarray should be an object with the following properties:
*
*     -   **dtype**: data type.
*     -   **data**: data buffer.
*     -   **shape**: dimensions.
*     -   **strides**: stride lengths.
*     -   **offset**: index offset.
*     -   **order**: specifies whether an ndarray is row-major (C-style) or column major (Fortran-style).
*
* @param {ArrayLikeObject<Object>} arrays - array-like object containing one input array and a zero-dimensional search element array
* @returns {boolean} result
*
* @example
* import Float64Array from '@stdlib/array-float64';
*
* // Create a data buffer:
* var xbuf = new Float64Array( [ 1.0, 2.0, 3.0, 4.0, 5.0, 6.0, 7.0, 8.0, 9.0, 10.0, 11.0, 12.0 ] );
*
* // Define the shape of the input array:
* var shape = [ 3, 1, 2 ];
*
* // Define the array strides:
* var sx = [ 4, 4, 1 ];
*
* // Define the index offset:
* var ox = 0;
*
* // Create the input ndarray-like object:
* var x = {
*     'dtype': 'float64',
*     'data': xbuf,
*     'shape': shape,
*     'strides': sx,
*     'offset': ox,
*     'order': 'row-major'
* };
*
* // Create the search element ndarray-like object:
* var searchElement = {
*     'dtype': 'float64',
*     'data': new Float64Array( [ 2.0 ] ),
*     'shape': [],
*     'strides': [ 0 ],
*     'offset': 0,
*     'order': 'row-major'
* };
*
* // Perform reduction:
* var out = includes( [ x, searchElement ] );
* // returns true
*/
function includes( arrays ) {
	var isCmplx;
	var value;
	var ndims;
	var xmmv;
	var shx;
	var iox;
	var len;
	var sx;
	var ox;
	var ns;
	var x;
	var d;
	var i;

	// Unpack the ndarrays and standardize ndarray meta data:
	x = ndarray2object( arrays[ 0 ] );
	value = ndarray2object( arrays[ 1 ] );

	shx = x.shape;
	ndims = shx.length;

	// Resolve the search element as a scalar:
	value = value.accessors[ 0 ]( value.data, value.offset );

	// Check for known array types which can be reinterpreted for better iteration performance...
	if ( isBooleanArray( x.data ) ) {
		if ( !isBoolean( value ) ) {
			return false;
		}
		x.data = reinterpretBoolean( x.data, 0 );
		x.accessorProtocol = false;
	} else if ( isComplexArray( x.data ) ) {
		if ( !isComplexLike( value ) ) {
			return false;
		}
		// TODO: consider adding something like `complex/base/complex2object` where we normalize a user-provided complex-like object to a standardized object shape
		value = {
			're': real( value ),
			'im': imag( value )
		};
		x.data = reinterpretComplex( x.data, 0 );
		x.accessorProtocol = false;
		x.strides = gscal( ndims, 2, x.strides, 1 );
		x.offset *= 2;
		isCmplx = true;
	}
	// Determine whether we can avoid iteration altogether...
	if ( ndims === 0 ) {
		if ( x.accessorProtocol ) {
			return ACCESSOR_INCLUDES[ ndims ]( x, value );
		}
		if ( isCmplx ) {
			return COMPLEX_INCLUDES[ ndims ]( x, value );
		}
		return INCLUDES[ ndims ]( x, value );
	}
	// Compute the number of elements and the number of singleton dimensions...
	len = 1; // number of elements
	ns = 0;  // number of singleton dimensions
	for ( i = 0; i < ndims; i++ ) {
		d = shx[ i ];

		// Note that, if one of the dimensions is `0`, the length will be `0`...
		len *= d;

		// Check whether the current dimension is a singleton dimension...
		if ( d === 1 ) {
			ns += 1;
		}
	}
	// Check whether we were provided an empty ndarray...
	if ( len === 0 ) {
		// An empty array is a trivial case in which an array does not contain a search element:
		return false;
	}
	// Determine whether the ndarray is one-dimensional and thus readily translates to a one-dimensional strided array...
	if ( ndims === 1 ) {
		if ( x.accessorProtocol ) {
			return ACCESSOR_INCLUDES[ ndims ]( x, value );
		}
		if ( isCmplx ) {
			return COMPLEX_INCLUDES[ ndims ]( x, value );
		}
		return INCLUDES[ ndims ]( x, value );
	}
	sx = x.strides;

	// Determine whether the ndarray has only **one** non-singleton dimension (e.g., ndims=4, shape=[10,1,1,1]) so that we can treat an ndarray as being equivalent to a one-dimensional strided array...
	if ( ns === ndims-1 ) {
		// Get the index of the non-singleton dimension...
		for ( i = 0; i < ndims; i++ ) {
			if ( shx[ i ] !== 1 ) {
				break;
			}
		}
		x.shape = [ shx[i] ];
		x.strides = [ sx[i] ];
		if ( x.accessorProtocol ) {
			return ACCESSOR_INCLUDES[ 1 ]( x, value );
		}
		if ( isCmplx ) {
			return COMPLEX_INCLUDES[ 1 ]( x, value );
		}
		return INCLUDES[ 1 ]( x, value );
	}
	iox = iterationOrder( sx ); // +/-1

	// Determine whether we can avoid blocked iteration...
	if ( iox !== 0 ) {
		// Determine the minimum and maximum linear indices which are accessible by the array view:
		xmmv = minmaxViewBufferIndex( shx, sx, x.offset );

		// Determine whether we can ignore shape (and strides) and treat the ndarray as a linear one-dimensional strided array...
		if ( len === ( xmmv[1]-xmmv[0]+1 ) || ( isCmplx && len*2 === ( xmmv[1]-xmmv[0]+2 ) ) ) { // eslint-disable-line max-len
			// Note: the above is equivalent to @stdlib/ndarray/base/assert/is-contiguous, but in-lined so we can retain computed values...
			if ( iox === 1 ) {
				ox = xmmv[ 0 ];
			} else {
				ox = xmmv[ 1 ];
			}
			x.shape = [ len ];
			x.strides = [ ( isCmplx ) ? iox*2 : iox ];
			x.offset = ox;
			if ( x.accessorProtocol ) {
				return ACCESSOR_INCLUDES[ 1 ]( x, value );
			}
			if ( isCmplx ) {
				return COMPLEX_INCLUDES[ 1 ]( x, value );
			}
			return INCLUDES[ 1 ]( x, value );
		}
		// The ndarray is non-contiguous, so we cannot directly use one-dimensional array functionality...

		// Determine whether we can use simple nested loops...
		if ( ndims <= MAX_DIMS ) {
			// So long as iteration always moves in the same direction (i.e., no mixed sign strides), we can leverage cache-optimal (i.e., normal) nested loops without resorting to blocked iteration...
			if ( x.accessorProtocol ) {
				return ACCESSOR_INCLUDES[ ndims ]( x, value );
			}
			if ( isCmplx ) {
				return COMPLEX_INCLUDES[ ndims ]( x, value );
			}
			return INCLUDES[ ndims ]( x, value );
		}
		// Fall-through to blocked iteration...
	}
	// At this point, we're either dealing with a non-contiguous n-dimensional array or a high dimensional n-dimensional array, so our only hope is that we can still perform blocked iteration...

	// Determine whether we can perform blocked iteration...
	if ( ndims <= MAX_DIMS ) {
		if ( x.accessorProtocol ) {
			return BLOCKED_ACCESSOR_INCLUDES[ ndims-2 ]( x, value );
		}
		if ( isCmplx ) {
			return BLOCKED_COMPLEX_INCLUDES[ ndims-2 ]( x, value );
		}
		return BLOCKED_INCLUDES[ ndims-2 ]( x, value );
	}
	// Fall-through to linear view iteration without regard for how data is stored in memory (i.e., take the slow path)...
	if ( x.accessorProtocol ) {
		return accessorincludesnd( x, value );
	}
	if ( isCmplx ) {
		return complexincludesnd( x, value );
	}
	return includesnd( x, value );
}


// EXPORTS //

export default includes;
